/* ===========================================================
   Lucraa — Loja-VITRINE da landing pública (NÃO é a demo do app).
   Decisão de marketing: a landing precisa de um exemplo de PORTE
   médio/grande (≈ R$ 300k/mês, em CRESCIMENTO) para o lojista-alvo
   se reconhecer — R$ 50k passava "loja pequena". É uma loja de
   exemplo separada, servida só pelo getLanding (src/api/client.ts);
   a loja-demo do app ("Koloni Skate Shop", koloni-skateshop.ts) e
   seus testes ficam intactos.

   Mesma disciplina da loja-demo: NADA pode se contradizer entre as
   seções da landing — as invariantes estão em landing-vitrine.test.ts.
   A NARRATIVA de diagnóstico é preservada de propósito (é a prova de
   venda): ainda há 1 campanha no vermelho (#problema), ROAS informado
   > real (triângulo) e dinheiro a recuperar (monitores) — só que em
   escala de loja grande e com a tendência do mês para CIMA.

   O catálogo de PREÇO (faixas f1–f5) é reusado da loja-demo: é o mesmo
   plano comercial para qualquer loja; só a faixa detectada muda (porte
   ≈ R$ 300k → f4). Os números financeiros são SINTÉTICOS.
   =========================================================== */
import type { Ads, Derivados, LandingData, LojaInfo, WaterfallStep } from "../api/types";
import { faixaParaFaturamento } from "../domain/faixa";
import { breakevenRoas } from "../domain/roas";
import { KOLONI_SKATESHOP } from "./koloni-skateshop";

/* ---- Loja (Nuvemshop) — porte grande, em crescimento ----
   coerência: ticketMedio = receita ÷ pedidos (300.000 ÷ 1.200) e
   receita líquida + descontos = bruta (318.000) — ver o .test.ts. */
const nuvemshop: LojaInfo = {
  loja: "Koloni Skate Shop",
  segmento: "Skate e streetwear",
  periodo: "mês corrente",
  receita: 300000,
  descontos: 18000, // cupons do período; receita (líquida) + descontos = bruta (318.000)
  pedidos: 1200,
  ticketMedio: 250.0,
  conversao: 0.021,
  produtos: [
    {
      nome: "Tênis Tesla Flow Black/White",
      vendas: 290,
      margem: 0.44,
      be: 2.3,
      gastoAds: 16000,
      fonteGastoAds: "catalogo" as const,
      roasReal: 6.8,
      roasPlat: 8.4,
      cpa: 58,
      conv: 0.03,
      estoqueDias: 12,
      faturamento: 86000,
      visualizacoes: 9800,
    },
    {
      nome: "Camiseta Huf Ice Dice",
      vendas: 980,
      margem: 0.21,
      be: 4.8,
      gastoAds: 14000,
      fonteGastoAds: "destino_anuncio" as const,
      roasReal: 5.6,
      roasPlat: 7.0,
      cpa: 22,
      conv: 0.046,
      estoqueDias: 28,
      faturamento: 79000,
      visualizacoes: 22000,
    },
    {
      nome: "Shape Maple Koloni Santa",
      vendas: 210,
      margem: 0.46,
      be: 2.2,
      gastoAds: 12000,
      fonteGastoAds: "catalogo" as const,
      roasReal: 6.1,
      roasPlat: 7.6,
      cpa: 60,
      conv: 0.026,
      estoqueDias: 9,
      faturamento: 64000,
      visualizacoes: 8400,
    },
    {
      nome: "Skate Montado Hondar Pro Setup",
      vendas: 120,
      margem: 0.4,
      be: 2.5,
      gastoAds: 10000,
      fonteGastoAds: "destino_anuncio" as const,
      roasReal: 5.2,
      roasPlat: 6.8,
      cpa: 84,
      conv: 0.012,
      estoqueDias: 22,
      faturamento: 47000,
      visualizacoes: 12000,
    },
  ],
  carrinhosAbandonados: { valorRecuperavel: 3600, qtd: 32 },
  clientesInativos90d: { qtd: 540, potencialRecuperacao: 96000 },
  // coerência: receita − cmv − frete − imposto − embalagem = margem de contribuição (128.400);
  // − anúncios (54.000) = lucro das vendas (74.400). Ver waterfall + .test.ts.
  custos: { cmv: 126000, frete: 15600, imposto: 21000, embalagem: 9000 },
};

/* ---- Ads — mapeamento automático (decisions.md §18) ----
   Mantém a tese da landing: 1 campanha no vermelho ("Coleção Inverno")
   alimenta a seção #problema e o monitor de anúncios. As demais lucram
   forte (loja saudável). Invariantes: Meta + Google = total; campanhas +
   gastoSemCampanha = total; balde no canal Meta; Google fecha exato. */
const ads: Ads = {
  investimentoTotal: 54000,
  investimentoMeta: 27000,
  investimentoGoogle: 27000,
  campanhas: [
    {
      externalId: "23861940872110456",
      provider: "meta_ads" as const,
      plataforma: "Meta",
      nome: "Coleção Inverno",
      investido: 12000,
      roasInformado: 3.6,
      roasReal: 2.0, // abaixo do equilíbrio (≈ 2,3) → queima margem
      lucroLiquido: -6400,
      vinculo: "utm_id" as const,
    },
    {
      externalId: "23861977553420456",
      provider: "meta_ads" as const,
      plataforma: "Meta",
      nome: "Retargeting Dinâmico",
      investido: 14000,
      roasInformado: 8.2,
      roasReal: 7.0,
      lucroLiquido: 21000,
      vinculo: "auto_append" as const,
    },
    {
      externalId: "21598437621",
      provider: "google_ads" as const,
      plataforma: "Google",
      nome: "Search Marca",
      investido: 9000,
      roasInformado: 11.5,
      roasReal: 10.2,
      lucroLiquido: 19000,
      vinculo: "gclid" as const,
    },
    {
      externalId: "21601229048",
      provider: "google_ads" as const,
      plataforma: "Google",
      nome: "Performance Max",
      investido: 18000,
      roasInformado: 6.0,
      roasReal: 4.9,
      lucroLiquido: 14000,
      vinculo: "gclid" as const,
    },
  ],
  // balde do canal Meta (cliques antigos sem utm_id) + parcela genérica que
  // não desce a produto. Campanhas (53.000) + balde (1.000) = total (54.000).
  gastoSemCampanha: 1000,
  gastoNaoAtribuivelProduto: 2000,
};

/* ---- Composição do lucro (waterfall) — o coração da landing ----
   Da receita bruta (318.000) ao lucro das vendas (74.400). Cada degrau
   bate com a sua fonte (nuvemshop.custos / ads); a soma dos degraus
   (sem subtotal/total) = lucro das vendas. */
const waterfall: WaterfallStep[] = [
  { rotulo: "Receita bruta", valor: 318000, tipo: "base" },
  { rotulo: "Descontos", valor: -18000, tipo: "sub" },
  { rotulo: "CMV", valor: -126000, tipo: "sub" },
  { rotulo: "Frete", valor: -15600, tipo: "sub" },
  { rotulo: "Imposto", valor: -21000, tipo: "sub" },
  { rotulo: "Embalagens", valor: -9000, tipo: "sub" },
  // marco: receita líquida − custos variáveis, antes da mídia.
  { rotulo: "Margem de contribuição", valor: 128400, tipo: "subtotal" },
  { rotulo: "Anúncios", valor: -54000, tipo: "sub" },
  { rotulo: "Lucro das vendas", valor: 74400, tipo: "total" },
];

/* ---- Série de lucro · últimos 30 dias ----
   Tendência de ALTA (sentimento de crescimento); fecha no lucro de hoje
   (índice 29) com ontem no penúltimo (índice 28) — ver lucroPeriodos.hoje. */
const lucroSerie = [
  2000, 2080, 2050, 2160, 2120, 2220, 2200, 2150, 2280, 2260, 2320, 2300, 2380, 2360, 2300, 2420,
  2400, 2460, 2440, 2380, 2500, 2480, 2540, 2520, 2580, 2560, 2620, 2600, 2480, 2600,
].map((valor, i) => ({ dia: i + 1, valor }));

const derivados: Derivados = {
  lucroLiquido: 74400,
  margemLiquida: 0.248, // 74.400 ÷ 300.000
  roasMedio: 7.0, // ROAS informado blended (≈ Σ investido×roasInformado ÷ gasto)
  roasReal: 5.6, // ROAS real blended — sempre abaixo do informado
  // breakeven blended: 300.000 ÷ (300.000 − 171.600) ≈ 2,34x
  roasEquilibrio: breakevenRoas(
    nuvemshop.receita,
    nuvemshop.custos.cmv +
      nuvemshop.custos.frete +
      nuvemshop.custos.imposto +
      nuvemshop.custos.embalagem,
  ),
  deltaLucro: 0.16, // mês em crescimento
  deltaReceita: 0.12,
  deltaConversao: 0.08,
  // coerência: soma dos `potencial` dos monitores (3.600 + 22.000 + 6.400)
  recuperavelMes: 32000,
  respostaDoDia:
    "Reponha o Shape Maple Koloni Santa (zera em 7 dias) e pause a Meta “Coleção Inverno” — ela está R$ 6.400 no vermelho neste mês.",
  lucroPeriodos: {
    hoje: {
      label: "Hoje",
      receita: 10400,
      lucro: 2600,
      anterior: { label: "ontem", receita: 9900, lucro: 2480 },
    },
    "7d": {
      label: "Últimos 7 dias",
      receita: 71000,
      lucro: 17600,
      anterior: { label: "7 dias anteriores", receita: 67000, lucro: 16400 },
    },
    "30d": {
      label: "Últimos 30 dias",
      receita: 320000,
      lucro: 79000,
      anterior: { label: "30 dias anteriores", receita: 298000, lucro: 73000 },
    },
    "este-mes": {
      label: "Este mês",
      receita: 300000,
      lucro: 74400,
      anterior: { label: "mês passado", receita: 268000, lucro: 64000 },
    },
    "mes-passado": {
      label: "Mês passado",
      receita: 268000,
      lucro: 64000,
      anterior: { label: "dois meses atrás", receita: 250000, lucro: 59000 },
    },
    "90d": {
      label: "Últimos 90d",
      receita: 900000,
      lucro: 218000,
      anterior: { label: "90 dias anteriores", receita: 840000, lucro: 202000 },
    },
  },
  monitores: [
    {
      id: "vendas",
      tag: "Vendas",
      icone: "cart",
      titulo: "32 carrinhos abandonados nas últimas 48h",
      resumo:
        "Clientes que abandonaram o checkout nas últimas 48 horas. Recuperação manual via cupom converte 15–25%.",
      valorRisco: 14800,
      valorLabel: "parados no checkout",
      insight:
        "Dispare um cupom de 10% para os 32 clientes — recuperando 15–25%, são R$ 2.200 a R$ 3.600 de volta ainda hoje.",
      cta: "Criar campanha",
      tipo: "campanha",
      potencial: 3600,
      detalheCta: "Ver detalhes",
      detalheTitulo: "Carrinhos abandonados · últimas 48h",
    },
    {
      id: "estoque",
      tag: "Estoque",
      icone: "box",
      titulo: "5 produtos vão zerar o estoque em até 7 dias",
      resumo:
        "Monitoro todos os produtos e projeto quando o estoque acaba com base na média de vendas dos últimos 30 dias.",
      valorRisco: 22000,
      valorLabel: "de receita em risco",
      insight:
        "Reponha o Shape Maple Koloni Santa primeiro — é a maior receita em risco e o fornecedor leva 5 dias úteis.",
      cta: "Marcar reposto",
      tipo: "feito",
      potencial: 22000,
      detalheCta: "Ver detalhes",
      detalheTitulo: "Produtos com estoque crítico · próximos 7 dias",
    },
    {
      id: "anuncios",
      tag: "Anúncios",
      icone: "chart",
      titulo: "1 campanha ativa está abaixo do breakeven",
      resumo:
        "Monitoro todas as campanhas ativas e comparo o ROAS real (descontando custos) com o ROAS de equilíbrio da loja.",
      valorRisco: 6400,
      valorLabel: "de prejuízo no mês",
      insight:
        "Pause a Meta “Coleção Inverno” (ROAS real 2,0, abaixo do equilíbrio 2,3) — só isso estanca R$ 6.400/mês.",
      cta: "Marcar pausada",
      tipo: "feito",
      potencial: 6400,
      detalheCta: "Mais detalhes",
      detalheTitulo: "Campanhas que perderam performance",
    },
  ],
};

// Faixa detectada pelo porte (média 90d ÷ 3 = R$ 300.000/mês → f4). Reusa o
// MESMO catálogo de preço da loja-demo (plano comercial é único — só a faixa muda).
const faturamentoMensalMedio = derivados.lucroPeriodos["90d"]!.receita / 3;
const faixaAtual = faixaParaFaturamento(KOLONI_SKATESHOP.plano.faixas, faturamentoMensalMedio);

export const LANDING_VITRINE: LandingData = {
  nuvemshop,
  derivados,
  waterfall,
  lucroSerie,
  ads,
  plano: KOLONI_SKATESHOP.plano,
  faixaDetectada: { faturamentoMensalMedio, faixaId: faixaAtual.id },
};
